import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'motion/react'
import AuthWindow from '@/components/AuthWindow'
import StippledBackground from '@/components/StippledBackground'
import { useThemeMode } from '@/contexts/ThemeContext'
import { verifyEmail, verifyInvite } from '@/lib/auth'
import { supabase } from '@/lib/api/client'
import { clearGuestSession, loadGuestSession } from '@/lib/guestStorage'
import { theme as defaultTheme } from '@/lib/theme'

// Simplified TopNav for auth page
function SimpleTopNav({ onThemeToggle }: { onThemeToggle: () => void }) {
  const { isDark, colors } = useThemeMode()

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '40px',
        backgroundColor: colors.navBg,
        borderBottom: `1px solid ${colors.navBorder}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 12px',
        zIndex: 1000,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        fontFamily: 'var(--font-departure-mono)',
      }}
    >
      {/* Logo */}
      <a
        href="https://honcho.chat"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          fontSize: '14px',
          fontWeight: 'bold',
          color: colors.navItemText,
          letterSpacing: '1px',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <svg width="20" height="18" viewBox="0 0 87 77" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginTop: '-2px' }}>
          <path d="M55.29 0H38.77V6.89H55.29V0Z" fill="currentColor"/>
          <path d="M65.95 6.89H55.29V13.69H65.95V6.89Z" fill="currentColor"/>
          <path d="M72.66 13.69H65.95V20.4H72.66V13.69Z" fill="currentColor"/>
          <path d="M79.54 20.4H72.66V30.17H79.54V20.4Z" fill="currentColor"/>
          <path d="M86.33 30.16H79.54V46.54H86.33V30.16Z" fill="currentColor"/>
          <path d="M79.54 46.55H72.67V56.32H79.54V46.55Z" fill="currentColor"/>
          <path d="M72.67 56.31H65.95V63.11H72.67V56.31Z" fill="currentColor"/>
          <path d="M65.95 63.11H55.29V69.96H65.95V63.11Z" fill="currentColor"/>
          <path d="M55.29 69.96H35.84V76.65H55.29V69.96Z" fill="currentColor"/>
          <path d="M35.84 63.11H25.18V69.96H35.84V63.11Z" fill="currentColor"/>
          <path d="M25.18 56.31H18.39V63.11H25.18V56.31Z" fill="currentColor"/>
          <path d="M18.39 46.55H11.52V56.32H18.39V46.55Z" fill="currentColor"/>
          <path d="M4.87 33.97V20.4H0V40.8H4.87V46.55H11.52V33.97H4.87Z" fill="currentColor"/>
          <path d="M18.31 27.17H11.52V33.96H18.31V27.17Z" fill="currentColor"/>
          <path d="M38.77 6.89H18.39V13.69H38.77V6.89Z" fill="currentColor"/>
          <path d="M45.5 13.69H38.79V20.4H45.5V13.69Z" fill="currentColor"/>
          <path d="M18.32 27.17H38.79V20.4H18.39V13.69H4.87V20.4H18.32V27.17Z" fill="currentColor"/>
          <path d="M38.79 30.16H31.98V36.87H38.79V30.16Z" fill="currentColor"/>
          <path d="M59.09 30.16H52.28V36.87H59.09V30.16Z" fill="currentColor"/>
          <path d="M59.16 50.47H31.98V56.31H59.16V50.47Z" fill="currentColor"/>
        </svg>
        Honcho Chat
      </a>

      {/* Right side controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Theme Toggle */}
        <button
          onClick={onThemeToggle}
          style={{
            padding: '4px 8px',
            background: 'transparent',
            border: `1px solid ${colors.primaryBorder}`,
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            color: colors.primaryText,
            fontSize: '14px',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.secondaryBg
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent'
          }}
        >
          {isDark ? '🌙' : '☀︎'}
        </button>
      </div>
    </div>
  )
}

export default function AuthPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { colors, toggleTheme } = useThemeMode()
  const [isVerifying, setIsVerifying] = useState<boolean | null>(null)
  const [verificationError, setVerificationError] = useState<string | null>(
    null
  )

  // Check if we're on the reset-password path
  const isResetPasswordPath = window.location.pathname.includes('/reset-password')

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      // Check for email confirmation or invite token
      const tokenHash = searchParams.get('token_hash')
      const type = searchParams.get('type')

      if (tokenHash && (type === 'email' || type === 'invite')) {
        setIsVerifying(true)
        try {
          // Use backend-mediated verification
          const result = type === 'email'
            ? await verifyEmail(tokenHash, type)
            : await verifyInvite(tokenHash, type)

          if (result.error) {
            console.error('Verification error:', result.error)
            setVerificationError(result.error)
            setIsVerifying(false)
            return
          }

          if (result.session) {
            // Session is already stored via Supabase SDK in the verification functions

            // Check if there's a guest session to migrate
            const guestSession = loadGuestSession()
            if (guestSession?.messages?.length) {
              try {
                // Migrate guest session to backend
                const messages = guestSession.messages.map((msg) => ({
                  role: msg.role,
                  content: msg.content,
                }))

                const response = await fetch(
                  `${import.meta.env.VITE_API_URL}/api/chat/create-session-with-messages`,
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${result.session.access_token}`,
                    },
                    body: JSON.stringify({
                      sessionId: 'default',
                      messages,
                    }),
                  }
                )

                if (!response.ok) {
                  console.error('Failed to migrate guest session')
                }

                // Clear guest session after successful migration
                clearGuestSession()
              } catch (err) {
                console.error('Error migrating guest session:', err)
                // Still clear guest session even if migration fails
                clearGuestSession()
              }
            }

            // Redirect to home (chat)
            navigate('/', { replace: true })
            return
          }

          setVerificationError('No session returned after verification')
        } catch (err) {
          console.error('Verification error:', err)
          setVerificationError('An error occurred during verification')
        } finally {
          setIsVerifying(false)
        }
        return
      } else {
        setIsVerifying(false)
      }

      // Check if already authenticated (check for Supabase session)
      const { data: { session } } = await supabase.auth.getSession()
      if (session) {
        navigate('/')
      }
    }

    handleEmailConfirmation()
  }, [navigate, searchParams])

  const handleAuthSuccess = () => {
    navigate('/')
  }

  if (isVerifying) {
    return (
      <div
        style={{
          position: 'relative',
          minHeight: '100vh',
          fontFamily: 'var(--font-departure-mono)',
        }}
      >
        <StippledBackground />
        <SimpleTopNav onThemeToggle={toggleTheme} />

        <div
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            minHeight: '100vh',
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: '40px',
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            style={{
              width: '100%',
              maxWidth: '350px',
              padding: defaultTheme.spacing.lg,
              backgroundColor: colors.primaryBg,
              border: `1px solid ${colors.primaryBorder}`,
              borderRadius: '0',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <h2
                style={{
                  fontSize: defaultTheme.fontSize.lg,
                  fontWeight: '600',
                  color: colors.primaryText,
                  marginBottom: defaultTheme.spacing.sm,
                }}
              >
                Verifying your email...
              </h2>
              <p
                style={{
                  fontSize: defaultTheme.fontSize.sm,
                  color: colors.secondaryText,
                }}
              >
                Please wait while we confirm your account.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    )
  }

  if (verificationError) {
    return (
      <div
        style={{
          position: 'relative',
          minHeight: '100vh',
          fontFamily: 'var(--font-departure-mono)',
        }}
      >
        <StippledBackground />
        <SimpleTopNav onThemeToggle={toggleTheme} />

        <div
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            minHeight: '100vh',
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: '40px',
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            style={{
              width: '100%',
              maxWidth: '350px',
              padding: defaultTheme.spacing.lg,
              backgroundColor: colors.primaryBg,
              border: `1px solid ${colors.primaryBorder}`,
              borderRadius: '0',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <h2
                style={{
                  fontSize: defaultTheme.fontSize.lg,
                  fontWeight: '600',
                  color: colors.primaryText,
                  marginBottom: defaultTheme.spacing.sm,
                }}
              >
                Verification Failed
              </h2>
              <p
                style={{
                  fontSize: defaultTheme.fontSize.sm,
                  color: colors.errorBorder,
                  marginBottom: defaultTheme.spacing.md,
                }}
              >
                {verificationError}
              </p>
              <button
                onClick={() => navigate('/auth')}
                style={{
                  backgroundColor: colors.primaryAccent,
                  color: colors.primaryBg,
                  padding: `${defaultTheme.spacing.sm} ${defaultTheme.spacing.md}`,
                  fontSize: defaultTheme.fontSize.sm,
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  borderRadius: '4px',
                }}
              >
                Back to Login
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    )
  }

  if (isVerifying === null) {
    return (
      <div
        style={{
          position: 'relative',
          minHeight: '100vh',
          fontFamily: 'var(--font-departure-mono)',
        }}
      >
        <StippledBackground />
        <SimpleTopNav onThemeToggle={toggleTheme} />

        <div
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            minHeight: '100vh',
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: '40px',
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            style={{
              width: '100%',
              maxWidth: '350px',
              padding: defaultTheme.spacing.lg,
              backgroundColor: colors.primaryBg,
              border: `1px solid ${colors.primaryBorder}`,
              borderRadius: '0',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <h2
                style={{
                  fontSize: defaultTheme.fontSize.lg,
                  fontWeight: '600',
                  color: colors.primaryText,
                }}
              >
                Loading...
              </h2>
            </div>
          </motion.div>
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100vh',
        fontFamily: 'var(--font-departure-mono)',
      }}
    >
      <StippledBackground />
      <SimpleTopNav onThemeToggle={toggleTheme} />

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
          paddingTop: '40px',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          style={{
            width: '100%',
            maxWidth: '350px',
            padding: defaultTheme.spacing.lg,
            backgroundColor: colors.primaryBg,
            border: `1px solid ${colors.primaryBorder}`,
            borderRadius: '0',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          }}
        >
          <AuthWindow
            onAuthSuccess={handleAuthSuccess}
            showFooter={true}
            extraData={isResetPasswordPath ? { mode: 'reset-password' } : undefined}
          />
        </motion.div>
      </div>
    </div>
  )
}
